// Versioned local save (spec §10). loadSave() never throws: anything unreadable becomes a fresh save.
import type { SaveData, RunResult, SaveSettings } from './types';

export const SAVE_KEY = 'sbs_range_v1';
export const SAVE_VERSION = 1 as const;
export const MAX_RUNS = 20;
/** Runs waiting for the global board (offline, transient failure); past this the oldest are dropped. */
export const MAX_PENDING = 20;
/** The board stores 1–24 characters; the same bound is applied here before anything is sent. */
export const NICKNAME_MAX = 24;
export const STARTER_CRAFT = ['trainer-172', 'sailboat-34'];

/** The subset of the Web Storage API the save needs; tests pass an in-memory one. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Schema migrations keyed by the version they upgrade FROM. v1 is the first schema, so the table is empty. */
export const MIGRATIONS: Record<number, (raw: Record<string, unknown>) => Record<string, unknown>> = {};

/** 'Pilot-0427': the default board name, so nobody has to type one before their first run. */
export function randomNickname(): string {
  return `Pilot-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
}

/** Trims, strips control characters and cuts to NICKNAME_MAX code points; empty or non-text → fallback. */
export function sanitizeNickname(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') return fallback;
  // eslint-disable-next-line no-control-regex
  const clean = Array.from(raw.replace(/[\u0000-\u001f\u007f]/g, '').trim()).slice(0, NICKNAME_MAX).join('').trim();
  return clean.length ? clean : fallback;
}

export function newSave(): SaveData {
  return {
    v: SAVE_VERSION,
    credits: 0,
    owned: [...STARTER_CRAFT],
    builds: {},
    bests: {},
    runs: [],
    pending: [],
    settings: { audio: true, volume: 0.8, reducedMotion: 'system', nickname: randomNickname(), online: true },
  };
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

function sanitizeSettings(raw: unknown, fallback: SaveSettings): SaveSettings {
  if (!isRecord(raw)) return fallback;
  const rm = raw.reducedMotion;
  return {
    audio: typeof raw.audio === 'boolean' ? raw.audio : fallback.audio,
    volume: typeof raw.volume === 'number' && Number.isFinite(raw.volume) ? Math.max(0, Math.min(1, raw.volume)) : fallback.volume,
    reducedMotion: rm === 'system' || rm === 'on' || rm === 'off' ? rm : fallback.reducedMotion,
    nickname: sanitizeNickname(raw.nickname, fallback.nickname),
    online: typeof raw.online === 'boolean' ? raw.online : fallback.online,
  };
}

/** Builds a valid SaveData from untrusted parsed JSON; unknown or malformed fields fall back to the fresh save. */
export function sanitizeSave(raw: unknown): SaveData {
  const fresh = newSave();
  if (!isRecord(raw)) return fresh;
  let data = raw;
  const v = typeof data.v === 'number' ? data.v : 0;
  for (let from = v; from < SAVE_VERSION; from++) {
    const migrate = MIGRATIONS[from];
    if (!migrate) return fresh;
    data = migrate(data);
  }
  const owned = isStringArray(data.owned) ? data.owned : fresh.owned;
  const builds: Record<string, string[]> = {};
  if (isRecord(data.builds)) for (const [k, val] of Object.entries(data.builds)) if (isStringArray(val)) builds[k] = val;
  const bests: Record<string, { nm: number; eff: number }> = {};
  if (isRecord(data.bests)) {
    for (const [k, val] of Object.entries(data.bests)) {
      if (isRecord(val) && typeof val.nm === 'number' && typeof val.eff === 'number') bests[k] = { nm: val.nm, eff: val.eff };
    }
  }
  const runs = Array.isArray(data.runs) ? (data.runs.filter(isRunResult) as RunResult[]).slice(0, MAX_RUNS) : [];
  // The queue is oldest first, so the newest survive the cap.
  const pending = Array.isArray(data.pending) ? (data.pending.filter(isRunResult) as RunResult[]).slice(-MAX_PENDING) : [];
  return {
    v: SAVE_VERSION,
    credits: typeof data.credits === 'number' && Number.isFinite(data.credits) ? Math.max(0, Math.floor(data.credits)) : 0,
    owned: Array.from(new Set([...STARTER_CRAFT, ...owned])),
    builds,
    bests,
    runs,
    pending,
    settings: sanitizeSettings(data.settings, fresh.settings),
  };
}

function isRunResult(v: unknown): boolean {
  if (!isRecord(v)) return false;
  return typeof v.craftId === 'string' && typeof v.distanceNm === 'number' && typeof v.credits === 'number' &&
    typeof v.seed === 'number' && (v.endedBy === 'dry-tank' || v.endedBy === 'player') && Array.isArray(v.events);
}

export function loadSave(storage: StorageLike | null = defaultStorage()): SaveData {
  if (!storage) return newSave();
  try {
    const text = storage.getItem(SAVE_KEY);
    if (!text) return newSave();
    return sanitizeSave(JSON.parse(text));
  } catch {
    return newSave();
  }
}

/** Returns false when nothing could be written (no storage, quota, private mode). */
export function persist(data: SaveData, storage: StorageLike | null = defaultStorage()): boolean {
  if (!storage) return false;
  try {
    storage.setItem(SAVE_KEY, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
}

export function resetSave(storage: StorageLike | null = defaultStorage()): SaveData {
  try {
    storage?.removeItem(SAVE_KEY);
  } catch {
    // nothing to do: the fresh save is returned either way
  }
  return newSave();
}

/** Folds a finished run into the save: credits, personal bests (sets isBest), last 20 runs. Pure: returns new objects. */
export function recordRun(save: SaveData, result: RunResult): { save: SaveData; result: RunResult } {
  const prev = save.bests[result.craftId];
  const isBest = !prev || result.distanceNm > prev.nm || result.efficiency > prev.eff;
  const stamped: RunResult = { ...result, isBest };
  const bests = { ...save.bests };
  bests[result.craftId] = {
    nm: Math.max(prev?.nm ?? 0, result.distanceNm),
    eff: Math.max(prev?.eff ?? 0, result.efficiency),
  };
  return {
    save: { ...save, credits: save.credits + Math.max(0, result.credits), bests, runs: [stamped, ...save.runs].slice(0, MAX_RUNS) },
    result: stamped,
  };
}
